import { useEffect, useState, useRef } from "react";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { DataTable } from "primereact/datatable";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { MultiSelect } from "primereact/multiselect";
import { Tag } from "primereact/tag";
import RawMaterialService from "../../../services/RawMaterials/RawMaterialService";
import ProductService from "../../../services/products/products.service";

interface Props {
  rawMaterials: any[];
  setRawMaterials: (data: any[]) => void;
  productMappingByRawMaterialId: Record<number, { rawMaterialId: number; productIds: number[]; productNames: string[] }>;
  setProductMappingByRawMaterialId: React.Dispatch<
    React.SetStateAction<Record<number, { rawMaterialId: number; productIds: number[]; productNames: string[] }>>
  >;
}

const RawMaterialDatatable: React.FC<Props> = ({
  rawMaterials,
  setRawMaterials,
  productMappingByRawMaterialId,
  setProductMappingByRawMaterialId,
}) => {
  const [materials, setMaterials] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [measurementTypes, setMeasurementTypes] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingRow, setEditingRow] = useState<any>(null);
  const [editName, setEditName] = useState("");
  const [editHsn, setEditHsn] = useState("");
  const [editCategoryId, setEditCategoryId] = useState<number | null>(null);
  const [editMeasurementTypeId, setEditMeasurementTypeId] = useState<number | null>(null);
  const [editProductIds, setEditProductIds] = useState<number[]>([]);
  const [editProductIdsInitialized, setEditProductIdsInitialized] = useState(false);
  const [productError, setProductError] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const toast = useRef<Toast>(null);

  useEffect(() => {
    setMaterials(rawMaterials || []);
  }, [rawMaterials]);

  useEffect(() => {
    const fetchDropdownData = async () => {
      const categoriesResponse = await RawMaterialService.RMCategories();
      const measurementTypesResponse = await RawMaterialService.RMMeasurementtypes();
      const productsResponse = await ProductService.getAllAdminProducts();

      if (categoriesResponse) {
        setCategories(categoriesResponse.map((c: any) => ({ label: c.categoryName, value: c.categoryID })));
      }

      if (measurementTypesResponse) {
        setMeasurementTypes(
          measurementTypesResponse.map((m: any) => ({
            label: m.measurementTypeName,
            value: m.measurementTypeID,
          }))
        );
      }

      if (productsResponse) {
        setProducts(
          productsResponse.map((p: any) => ({
            label: p.productName || p.name || p.title || `Product-${p.productID || p.productId || p.id || p.Id}`,
            value: p.productID || p.productId || p.id || p.Id,
          }))
        );
      }
    };

    fetchDropdownData();
  }, []);

  const showMessage = (label: string, message: string, severity: any) => {
    toast.current?.show({ severity, summary: label, detail: message, life: 3000 });
  };

  const toNumberArray = (value: any): number[] => {
    if (!Array.isArray(value)) return [];
    return value
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item) && item > 0);
  };

  const getRawMaterialId = (rowData: any) =>
    Number(rowData?.id ?? rowData?.Id ?? rowData?.rawMaterialId ?? rowData?.rawMaterialID ?? 0);

  const formatProductNames = (names: any) => {
    if (Array.isArray(names)) return names.filter(Boolean);
    if (typeof names === "string" && names.trim()) {
      return names.split(",").map((name) => name.trim()).filter(Boolean);
    }
    return [];
  };

  const openEditDialog = (rowData: any) => {
    setEditingRow(rowData);
    setEditName(rowData.name || "");
    setEditHsn(rowData.hsnCode || "");
    setEditCategoryId(rowData.rmCategoryId || null);
    setEditMeasurementTypeId(rowData.measurementTypeId || null);
    const rowId = getRawMaterialId(rowData);
    const mapping = rowId ? productMappingByRawMaterialId[rowId] : null;
    const pIds = toNumberArray(rowData.productIds || rowData.ProductIds || mapping?.productIds);
    if (pIds.length === 0 && rowData.productId) {
      pIds.push(rowData.productId);
    }
    setEditProductIds(pIds);
    setEditProductIdsInitialized(pIds.length > 0);
    setProductError("");
    setShowEditDialog(true);
  };

  const closeEditDialog = () => {
    setShowEditDialog(false);
    setEditingRow(null);
    setProductError("");
    setEditProductIdsInitialized(false);
  };

  useEffect(() => {
    if (!showEditDialog || !editingRow || editProductIdsInitialized) return;

    const rowId = getRawMaterialId(editingRow);
    const mapping = rowId ? productMappingByRawMaterialId[rowId] : null;
    const mappedIds = toNumberArray(mapping?.productIds);

    if (mappedIds.length > 0) {
      setEditProductIds(mappedIds);
      setEditProductIdsInitialized(true);
    }
  }, [showEditDialog, editingRow, productMappingByRawMaterialId, editProductIdsInitialized]);

  const handleUpdateSubmit = async () => {
    if (!editName.trim()) {
      showMessage("Validation", "Name is required", "error");
      return;
    }
    if (!editCategoryId) {
      showMessage("Validation", "Category is required", "error");
      return;
    }
    if (!editMeasurementTypeId) {
      showMessage("Validation", "Measurement Type is required", "error");
      return;
    }

    setIsUpdating(true);

    const getInt = (val: any) => {
      const parsed = parseInt(val, 10);
      return isNaN(parsed) ? 0 : parsed;
    };

    const rawMaterialId = getInt(
      editingRow?.id ??
        editingRow?.Id ??
        editingRow?.rawMaterialId ??
        editingRow?.rawMaterialID ??
        0
    );
    const categoryId = getInt(editCategoryId);
    const measurementTypeId = getInt(editMeasurementTypeId);
    const normalizedProductIds = toNumberArray(editProductIds);
    const normalizedProductNames = normalizedProductIds.map((id) => products.find((p) => p.value === id)?.label).filter(Boolean);

    const payload = {
      Id: rawMaterialId,
      id: rawMaterialId,
      rawMaterialId,
      rawMaterialID: rawMaterialId,
      Name: editName.trim(),
      RMCategoryID: categoryId,
      RMCategoryId: categoryId,
      rmCategoryId: categoryId,
      MeasurementTypeID: measurementTypeId,
      MeasurementTypeId: measurementTypeId,
      measurementTypeId,
      HSNCode: editHsn.trim(),
      ProductIds: normalizedProductIds,
      productIds: normalizedProductIds,
    };

    try {
      const response = await RawMaterialService.updateRawMaterial(payload);
      if (response && (response.isSuccess || response.success)) {
        showMessage("Update Raw Material", "Updated successfully!", "success");

        const updated = materials.map((m) => {
          const currentId = Number(m.id ?? m.Id ?? m.rawMaterialId ?? m.rawMaterialID);
          if (currentId === rawMaterialId) {
            return {
              ...m,
              name: editName,
              rmCategoryId: categoryId,
              measurementTypeId: measurementTypeId,
              hsnCode: editHsn,
              productIds: normalizedProductIds,
              productNames: normalizedProductNames.join(", "),
            };
          }
          return m;
        });

        setProductMappingByRawMaterialId((prev) => ({
          ...prev,
          [rawMaterialId]: {
            rawMaterialId,
            productIds: normalizedProductIds,
            productNames: normalizedProductNames,
          },
        }));

        setMaterials(updated);
        setRawMaterials(updated);
        closeEditDialog();
      } else {
        showMessage("Update Raw Material", response?.message || "Failed to update", "error");
      }
    } catch (error) {
      console.error("Error updating raw material:", error);
      showMessage("Update Raw Material", "Error occurred", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const confirmDelete = (rowData: any) => {
    confirmDialog({
      message: "Are you sure you want to delete this raw material?",
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => handleDelete(rowData),
    });
  };

  const categoryBodyTemplate = (rowData: any) => {
    const category = categories.find((cat) => cat.value === rowData.rmCategoryId);
    const label = category ? category.label : rowData.rmCategoryName;
    if (!label) return "-";
    return <Tag value={label} style={{ background: "#f8fafc", color: "#475569", border: "1px solid #e2e8f0", fontSize: "0.75rem", fontWeight: "500" }} />;
  };

  const measurementTypeBodyTemplate = (rowData: any) => {
    const measurementType = measurementTypes.find((mt) => mt.value === rowData.measurementTypeId);
    return measurementType ? measurementType.label : rowData.measurementTypeName || "-";
  };

  const productBodyTemplate = (rowData: any) => {
    const rowId = getRawMaterialId(rowData);
    const mapping = rowId ? productMappingByRawMaterialId[rowId] : null;
    const productsList =
      formatProductNames(mapping?.productNames).length > 0
        ? formatProductNames(mapping?.productNames)
        : formatProductNames(rowData.productNames).length > 0
          ? formatProductNames(rowData.productNames)
          : Array.isArray(rowData.products)
            ? rowData.products
                .map((product: any) => product.productName ?? product.name ?? product.title)
                .filter(Boolean)
            : rowData.productName || rowData.ProductName
              ? [rowData.productName || rowData.ProductName]
              : [];

    if (productsList.length === 0) return <span className="text-400">-</span>;

    return (
      <div className="flex flex-wrap gap-1 py-1" style={{ maxWidth: "300px" }}>
        {productsList.map((p: string, index: number) => (
          <Tag
            key={index}
            value={p}
            severity="success"
            style={{
              background: "#f0fdf4",
              color: "#166534",
              border: "1px solid #dcfce7",
              fontSize: "0.7rem",
              fontWeight: "500",
              padding: "2px 8px",
            }}
          />
        ))}
      </div>
    );
  };

  const handleDelete = async (rowData: any) => {
    try {
      const response = await RawMaterialService.deleteRawMaterial(rowData.id);
      if (response) {
        const updated = materials.filter((m) => m.id !== rowData.id);
        setMaterials(updated);
        setRawMaterials(updated);
        const rowId = getRawMaterialId(rowData);
        if (rowId) {
          setProductMappingByRawMaterialId((prev) => {
            const next = { ...prev };
            delete next[rowId];
            return next;
          });
        }
        showMessage("Delete Raw Material", "Deleted successfully!", "success");
      } else {
        showMessage("Delete Raw Material", "Failed to delete", "error");
      }
    } catch (error) {
      console.error("Error deleting raw material:", error);
      showMessage("Delete Raw Material", "Error occurred", "error");
    }
  };

  return (
    <div className="col-12">
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="card border-noround p-2">
        <DataTable
          value={materials}
          paginator
          rows={10}
          stripedRows
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          sortMode="multiple"
        >
          <Column field="name" header="Name" sortable />
          <Column field="rmCategoryId" header="Category" body={categoryBodyTemplate} sortable />
          <Column field="productId" header="Product" body={productBodyTemplate} sortable />
          <Column field="measurementTypeId" header="Measurement Type" body={measurementTypeBodyTemplate} />
          <Column field="hsnCode" header="HSN Code" sortable />
          <Column
            header="Action"
            body={(rowData) => (
              <Button
                icon="pi pi-pencil"
                onClick={() => openEditDialog(rowData)}
                className="p-button-rounded p-button-text p-button-success"
              />
            )}
            style={{ width: "8%" }}
          />
          <Column
            header="Delete"
            body={(rowData) => (
              <Button
                icon="pi pi-trash"
                onClick={() => confirmDelete(rowData)}
                className="p-button-rounded p-button-text p-button-danger"
              />
            )}
          />
        </DataTable>
      </div>

      <Dialog
        visible={showEditDialog}
        header="Edit Raw Material"
        style={{ width: "100%", maxWidth: "650px" }}
        modal
        onHide={closeEditDialog}
        className="minimal-batch-dialog"
        footer={
          <div className="minimal-dialog-footer">
            <Button label="Cancel" onClick={closeEditDialog} className="btn-minimal-cancel" disabled={isUpdating} />
            <Button label="Update Raw Material" onClick={handleUpdateSubmit} className="btn-minimal-save" loading={isUpdating} />
          </div>
        }
      >
        <style>{`
          .minimal-batch-dialog .p-dialog-header { background: #ffffff; padding: 2rem 2.5rem 1rem; border-bottom: none; }
          .minimal-batch-dialog .p-dialog-title { font-weight: 600; color: #111827; font-size: 1.5rem; letter-spacing: -0.02em; }
          .minimal-batch-dialog .p-dialog-content { padding: 0 !important; background: #ffffff; }
          .batch-custom-scroll { max-height: 65vh; overflow-y: auto; padding: 0 2.5rem 2rem !important; }
          .minimal-form-field { margin-bottom: 1.5rem; }
          .minimal-form-field label { font-size: 0.85rem; font-weight: 500; color: #6b7280; display: block; margin-bottom: 0.5rem; text-transform: uppercase; }
          .minimal-form-field .p-dropdown, .minimal-form-field .p-calendar .p-inputtext, .minimal-form-field .p-inputtext, .minimal-form-field .p-multiselect, .minimal-form-field .p-inputnumber {
              background: #f9fafb !important; border: 1px solid #f3f4f6 !important; border-radius: 12px !important; width: 100%; font-size: 0.95rem; color: #1f2937;
          }
          .minimal-form-field .p-inputtext, .minimal-form-field .p-dropdown .p-dropdown-label, .minimal-form-field .p-multiselect .p-multiselect-label {
              padding: 0.75rem 1rem !important;
          }
          .minimal-form-field .p-dropdown:hover, .minimal-form-field .p-inputtext:hover { background: #ffffff !important; border-color: #d1d5db !important; }
          .minimal-form-field .p-dropdown.p-focus, .minimal-form-field .p-inputtext:focus { background: #ffffff !important; border-color: #10B981 !important; box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1) !important; }
          .minimal-dialog-footer { display: flex; justify-content: flex-end; gap: 1rem; padding: 1.5rem 2.5rem; background: #ffffff; border-top: 1px solid #f3f4f6; }
          .btn-minimal-cancel { background: transparent !important; color: #6b7280 !important; border: none !important; font-weight: 500; padding: 0.75rem 1.5rem !important; border-radius: 999px !important; }
          .btn-minimal-save {
              background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%) !important;
              color: #ffffff !important;
              border: none !important;
              font-weight: 600 !important;
              padding: 0.75rem 2.5rem !important;
              border-radius: 999px !important;
              box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3) !important;
              transition: all 0.3s ease !important;
          }
          .btn-minimal-save:hover {
              transform: translateY(-2px) !important;
              box-shadow: 0 6px 16px rgba(76, 175, 80, 0.4) !important;
          }
          .required-star { color: #ef4444; margin-left: 2px; }
        `}</style>
        <div className="batch-custom-scroll">
          <div className="grid p-fluid mt-2">
            <div className="col-12 md:col-6 minimal-form-field">
              <label>
                Raw Material Name <span className="required-star">*</span>
              </label>
              <InputText value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Enter Raw Material Name" />
            </div>

            <div className="col-12 md:col-6 minimal-form-field">
              <label>HSN Code</label>
              <InputText
                value={editHsn}
                onChange={(e) => {
                  const regex = /^[A-Za-z0-9]*$/;
                  if (regex.test(e.target.value)) setEditHsn(e.target.value);
                }}
                placeholder="Enter HSN Code"
              />
            </div>

            <div className="col-12 md:col-6 minimal-form-field">
              <label>
                Category <span className="required-star">*</span>
              </label>
              <Dropdown
                value={editCategoryId}
                onChange={(e) => setEditCategoryId(e.value)}
                options={categories}
                placeholder="Select Category"
                filter
              />
            </div>

            <div className="col-12 md:col-6 minimal-form-field">
              <label>
                Measurement Type <span className="required-star">*</span>
              </label>
              <Dropdown
                value={editMeasurementTypeId}
                onChange={(e) => setEditMeasurementTypeId(e.value)}
                options={measurementTypes}
                placeholder="Select Measurement"
              />
            </div>

            <div className="col-12 minimal-form-field">
              <label>
                Associated Products <span className="required-star">*</span>
              </label>
              <MultiSelect
                value={editProductIds}
                onChange={(e) => {
                  setEditProductIds(toNumberArray(e.value));
                  setEditProductIdsInitialized(true);
                  if (e.value && e.value.length > 0) setProductError("");
                }}
                options={products}
                optionLabel="label"
                optionValue="value"
                placeholder="Select Products"
                filter
                display="chip"
                className={productError ? "p-invalid" : ""}
              />
              {productError && <small className="p-error">{productError}</small>}
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default RawMaterialDatatable;
